import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { GoogleLogin } from '@react-oauth/google';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../store/authSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { 
  KeyIcon, 
  ShieldCheckIcon, 
  EnvelopeIcon, 
  ArrowLeftIcon, 
  LockClosedIcon, 
  ExclamationCircleIcon, 
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const ForgotPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get('email') || '';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  const [email, setEmail] = useState(emailFromParams);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(emailFromParams ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordMessage, setPasswordMessage] = useState('');

  // Check password strength
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(0);
      setPasswordMessage('');
      return;
    }

    // Password strength criteria
    const hasLength = newPassword.length >= 8;
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    
    const strength = [hasLength, hasLowerCase, hasUpperCase, hasNumber, hasSpecialChar].filter(Boolean).length;
    
    setPasswordStrength(strength);
    
    if (strength <= 2) {
      setPasswordMessage('Weak password');
    } else if (strength <= 4) {
      setPasswordMessage('Medium password');
    } else {
      setPasswordMessage('Strong password');
    }
  }, [newPassword]);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      setError('');
      
      const response = await authService.requestPasswordReset(email);
      
      // Check if this is a Google account
      if (response.isGoogleAccount) {
        setIsGoogleAccount(true);
        setError(response.message);
        return;
      }
      
      setMessage(response.message);
      setStep(2);
    } catch (err: any) {
      setError(err.message || 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }
    
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }
    
    if (passwordStrength < 3) {
      setError('Please choose a stronger password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      setError('');
      
      const response = await authService.resetPassword(email, code, newPassword);
      
      // Check if this is a Google account
      if (response.isGoogleAccount) {
        setIsGoogleAccount(true);
        setError(response.message);
        return;
      }
      
      setMessage(response.message);
      setStep(3);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setCode(value);
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const response = await authService.googleLogin(credentialResponse.credential);
      dispatch(setCredentials(response));
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'Google login failed');
    }
  };

  const getProgressColor = () => {
    if (passwordStrength <= 2) return darkMode ? 'bg-red-500' : 'bg-red-500';
    if (passwordStrength <= 4) return darkMode ? 'bg-yellow-500' : 'bg-yellow-500';
    return darkMode ? 'bg-green-500' : 'bg-green-500';
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`max-w-md w-full space-y-8 p-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl`}
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 shadow-md shadow-indigo-500/30"
          >
            {step === 3 ? (
              <CheckCircleIcon className="h-8 w-8 text-white" />
            ) : (
              <KeyIcon className="h-8 w-8 text-white" />
            )}
          </motion.div>
          
          <h2 className="mt-6 text-center text-3xl font-extrabold">
            {step === 3 ? 'Password Reset Complete' : 'Reset Your Password'}
          </h2>
          
          <p className={`mt-2 text-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {step === 1 && "We'll send you a verification code to reset your password"}
            {step === 2 && "Enter the code sent to your email and create a new password"}
            {step === 3 && "Your password has been reset successfully"}
          </p>
        </div>
        
        <AnimatePresence mode="wait">
          {message && (
            <motion.div 
              key="message"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`rounded-lg p-4 flex items-center ${darkMode ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}
            >
              <CheckCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
              <p className="text-sm">{message}</p>
            </motion.div>
          )}
          
          {error && (
            <motion.div 
              key="error"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`rounded-lg p-4 flex items-center ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}
            >
              <ExclamationCircleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {isGoogleAccount ? (
            <motion.div 
              key="google-account"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-8 space-y-6"
            >
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  This email is associated with a Google account. Please sign in with Google:
                </p>
                <div className="flex justify-center mb-4">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google login failed')}
                    useOneTap
                  />
                </div>
              </div>
              <div className="text-center">
                <Link
                  to="/login"
                  className={`inline-flex items-center text-sm font-medium ${
                    darkMode 
                      ? 'text-indigo-400 hover:text-indigo-300' 
                      : 'text-indigo-600 hover:text-indigo-800'
                  } transition-colors duration-150`}
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to login
                </Link>
              </div>
            </motion.div>
          ) : step === 1 ? (
            <motion.form 
              key="step-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-8 space-y-6" 
              onSubmit={handleRequestReset}
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="email-address" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Email address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border ${error ? 'border-red-300' : darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${darkMode ? 'bg-gray-700 text-white placeholder-gray-400' : 'bg-white text-gray-900 placeholder-gray-400'}`}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-3">
                <motion.button
                  type="submit"
                  disabled={loading || !email.trim()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Request Reset Code'
                  )}
                </motion.button>
                
                <div className="text-center">
                  <Link
                    to="/login"
                    className={`inline-flex items-center text-sm font-medium ${
                      darkMode 
                        ? 'text-indigo-400 hover:text-indigo-300' 
                        : 'text-indigo-600 hover:text-indigo-800'
                    } transition-colors duration-150`}
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to login
                  </Link>
                </div>
              </div>
            </motion.form>
          ) : step === 2 ? (
            <motion.form 
              key="step-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-8 space-y-6" 
              onSubmit={handleResetPassword}
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="code" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Verification Code
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <ShieldCheckIcon className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      id="code"
                      name="code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      required
                      value={code}
                      onChange={handleCodeChange}
                      className={`block w-full pl-10 pr-3 py-3 border ${error && !code ? 'border-red-300' : darkMode ? 'border-gray-600' : 'border-gray-300'} tracking-widest text-center font-mono rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                      placeholder="123456"
                    />
                  </div>
                  <div className="text-xs text-right mt-1">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                      Enter the 6-digit code sent to your email
                    </span>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="new-password" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    New Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      id="new-password"
                      name="newPassword"
                      type="password"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border ${error && !newPassword ? 'border-red-300' : darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                      placeholder="New password"
                    />
                  </div>
                  
                  {newPassword && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                          <div 
                            className={`h-2.5 rounded-full ${getProgressColor()}`} 
                            style={{ width: `${(passwordStrength / 5) * 100}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs whitespace-nowrap ${
                          passwordStrength <= 2 
                            ? darkMode ? 'text-red-400' : 'text-red-500'
                            : passwordStrength <= 4 
                              ? darkMode ? 'text-yellow-400' : 'text-yellow-500'
                              : darkMode ? 'text-green-400' : 'text-green-500'
                        }`}>
                          {passwordMessage}
                        </span>
                      </div>
                      
                      <ul className="text-xs mt-2 grid grid-cols-2 gap-1">
                        <li className={`${newPassword.length >= 8 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                          • 8+ characters
                        </li>
                        <li className={`${/[A-Z]/.test(newPassword) ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                          • Uppercase letter
                        </li>
                        <li className={`${/[a-z]/.test(newPassword) ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                          • Lowercase letter
                        </li>
                        <li className={`${/\d/.test(newPassword) ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                          • Number
                        </li>
                        <li className={`${/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-gray-400' : 'text-gray-500')}`}>
                          • Special character
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="confirm-password" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Confirm Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className={`h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <input
                      id="confirm-password"
                      name="confirmPassword"
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border ${
                        error && newPassword !== confirmPassword 
                          ? 'border-red-300' 
                          : newPassword && newPassword === confirmPassword 
                            ? (darkMode ? 'border-green-500' : 'border-green-500') 
                            : (darkMode ? 'border-gray-600' : 'border-gray-300')
                      } rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                      placeholder="Confirm password"
                    />
                    {newPassword && confirmPassword && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {newPassword === confirmPassword ? (
                          <CheckCircleIcon className={`h-5 w-5 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                        ) : (
                          <ExclamationCircleIcon className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors duration-150`}
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back
                </button>
                
                <motion.button
                  type="submit"
                  disabled={loading || !code.trim() || !newPassword.trim() || !confirmPassword.trim() || newPassword !== confirmPassword}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`inline-flex items-center justify-center py-2 px-6 border border-transparent text-sm font-medium rounded-lg text-white ${loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </motion.button>
              </div>
              
              <div className="text-center pt-3 border-t">
                <button
                  type="button"
                  onClick={handleRequestReset}
                  className={`inline-flex items-center text-sm ${
                    darkMode 
                      ? 'text-indigo-400 hover:text-indigo-300' 
                      : 'text-indigo-600 hover:text-indigo-800'
                  } disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150`}
                  disabled={loading}
                >
                  <ArrowPathIcon className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Resend verification code
                </button>
              </div>
            </motion.form>
          ) : (
            <motion.div 
              key="step-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center mt-8 space-y-6"
            >
              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} flex flex-col items-center`}>
                <div className="w-16 h-16 mb-4 rounded-full bg-green-100 flex items-center justify-center text-green-500">
                  <CheckCircleIcon className="h-10 w-10" />
                </div>
                <p className="mb-4 font-medium">Your password has been reset successfully!</p>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>You can now log in with your new password</p>
              </div>
              
              <Link
                to="/login"
                className={`block w-full text-center py-3 px-4 rounded-lg text-sm font-medium ${
                  darkMode 
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                } shadow-sm hover:shadow transition-all duration-150`}
              >
                Back to Login
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      
      <p className={`mt-8 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Having trouble? Contact our support team at support@powerquizzer.com
      </p>
    </div>
  );
};

export default ForgotPassword;